/**
 * The Packer Bench contract: vial label printing through the ops API.
 *
 * Shapes mirror what the `elutide-vial-queue` mu-plugin returns over the ops bridge
 * (`/vial/products`, `/vial/jobs`, `/vial/enqueue`, `/vial/cancel`). The API passes them
 * through; the web renders them. Nothing here carries money, so every role that holds
 * `orders.print` sees the same payload.
 */
import { z } from 'zod';
export const VialJobStatusSchema = z.enum(['queued', 'printing', 'done', 'failed', 'cancelled']);
/** One current lot of one product, as the COA library records it. */
export const VialLotSchema = z.object({
    coa_id: z.number().int(),
    /** The lot exactly as stored in `_coa_lot_number`, e.g. "202608-RETA30". */
    batch: z.string(),
    /** `_coa_size` normalised to "<number> <unit>", e.g. "30 mg". */
    content: z.string(),
    is_current: z.boolean(),
    /** YYYY-MM-DD, or null when the certificate carries no date. */
    analysis_date: z.string().nullable(),
});
export const VialProductSchema = z.object({
    product_id: z.number().int(),
    /** The text printed as the product name, e.g. "GLOW". */
    label_name: z.string(),
    /** The COA product page slug, e.g. "glow". The QR target derives from the lot, not this. */
    slug: z.string(),
    /** Subline for blends, '' for single compounds. */
    blend: z.string(),
    lots: z.array(VialLotSchema),
});
/**
 * What the label printer agent last reported about itself. `seen_at` is unix seconds. The
 * plugin also records the agent's IP; it is not in this shape and `z.object` strips it, so
 * it never reaches the web.
 */
export const VialAgentSeenSchema = z.object({
    seen_at: z.number().nullable(),
    version: z.string(),
    printer: z.string(),
});
/** One row of `wp_elutide_vial_jobs`, as the bridge serialises it. */
export const VialJobSchema = z.object({
    id: z.number().int(),
    created_at: z.string(),
    updated_at: z.string(),
    source_ref: z.string(),
    requested_by: z.string(),
    product_id: z.number().int(),
    coa_id: z.number().int(),
    label_name: z.string(),
    content: z.string(),
    batch: z.string(),
    slug: z.string(),
    blend: z.string(),
    qty: z.number().int(),
    status: VialJobStatusSchema,
    printed_qty: z.number().int(),
    agent_note: z.string(),
    claimed_at: z.string().nullable(),
    finished_at: z.string().nullable(),
});
export const BenchVialProductsResponseSchema = z.object({
    products: z.array(VialProductSchema),
    agent: VialAgentSeenSchema,
});
export const BenchVialJobsResponseSchema = z.object({
    jobs: z.array(VialJobSchema),
    agent: VialAgentSeenSchema,
});
/** Body of `POST /bench/vial/jobs`. The lot is chosen by COA id; the client never types one. */
export const BenchVialEnqueueRequestSchema = z.object({
    coa_id: z.number().int().positive(),
    qty: z.number().int().min(1).max(200),
});
/**
 * `created` is what the plugin says about `/vial/enqueue`: false when `source_ref` already
 * had a row and that row came back instead of a new one. Absent on `/vial/cancel`.
 */
export const BenchVialJobResponseSchema = z.object({ job: VialJobSchema, created: z.boolean().optional() });
export const BenchVialJobsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=bench.js.map